import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PageHeader, Empty } from "@/components/PageHeader";
import { api, apiError } from "@/lib/api";
import { openInvoicePdf } from "@/lib/download";
import { fmtDate, money, today } from "@/lib/format";

const REASONS = ["Sales return", "Rate difference", "Post-sale discount", "Deficiency in service",
  "Goods rejected", "Short quantity received", "Other"];

export default function Notes() {
  const [tab, setTab] = useState("Credit Note");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [sources, setSources] = useState([]);
  const [srcId, setSrcId] = useState("");
  const [src, setSrc] = useState(null);
  const [qty, setQty] = useState({});
  const [head, setHead] = useState({ date: today(), reason: REASONS[0], narration: "" });
  const [busy, setBusy] = useState(false);

  const isCredit = tab === "Credit Note";

  const load = async () => {
    try {
      const { data } = await api.get(`/notes?type=${encodeURIComponent(tab)}`);
      setRows(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const startNew = async () => {
    setSrcId(""); setSrc(null); setQty({});
    setHead({ date: today(), reason: isCredit ? "Sales return" : "Goods rejected", narration: "" });
    try {
      const { data } = await api.get(`/vouchers?type=${isCredit ? "Sales" : "Purchase"}&limit=200`);
      setSources(data.filter((v) => v.status === "Posted"));
      setOpen(true);
    } catch (e) { toast.error(apiError(e)); }
  };

  const pickSource = async (id) => {
    setSrcId(id);
    try {
      const { data } = await api.get(`/notes/source/${id}`);
      setSrc(data);
      setQty({});
    } catch (e) { toast.error(apiError(e)); }
  };

  const est = useMemo(() => {
    if (!src) return 0;
    return src.lines.reduce((s, l) => {
      const q = Number(qty[l.line_index] || 0);
      const taxable = q * (l.rate || 0);
      return s + taxable * (1 + (l.gst_rate || 0) / 100);
    }, 0);
  }, [src, qty]);

  const save = async () => {
    const lines = Object.entries(qty)
      .filter(([, q]) => Number(q) > 0)
      .map(([i, q]) => ({ line_index: Number(i), qty: Number(q) }));
    if (!lines.length) { toast.error("Enter a return quantity on at least one line"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/notes/${isCredit ? "credit" : "debit"}`, {
        original_voucher_id: srcId, date: head.date, reason: head.reason,
        narration: head.narration || null, lines,
      });
      toast.success(`${tab} ${data.number} posted · ${money(data.amount)}`);
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="notes-page">
      <PageHeader title="Credit & Debit Notes"
        subtitle="Raised against an original invoice — reverses the ledger, the GST and the stock, and flows into GSTR-1 Table 9B.">
        <Button size="sm" onClick={startNew} data-testid="new-note-btn">New {isCredit ? "credit" : "debit"} note</Button>
      </PageHeader>

      <div className="flex gap-1 border-b border-border pb-2">
        {["Credit Note", "Debit Note"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`note-tab-${t.split(" ")[0].toLowerCase()}`}
            className={`px-2.5 py-1 text-xs ${tab === t ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}
            style={{ transitionProperty: "background-color, color" }}>
            {t}s {t === "Credit Note" ? "(sales return)" : "(purchase return)"}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <Empty label={`No ${tab.toLowerCase()}s yet`} hint={`Raise one against a posted ${isCredit ? "sales invoice" : "purchase bill"}.`} />
      ) : (
        <div className="overflow-x-auto border border-border">
          <table className="dense-table w-full min-w-[900px]" data-testid="notes-table">
            <thead>
              <tr><th>Date</th><th>Note no</th><th>Party</th><th>Against</th><th>Reason</th>
                <th className="text-right">Taxable</th><th className="text-right">Tax</th>
                <th className="text-right">Total</th><th /></tr>
            </thead>
            <tbody>
              {rows.map((n) => (
                <tr key={n.id} className="row-in" data-testid={`note-row-${n.id}`}>
                  <td className="num whitespace-nowrap">{fmtDate(n.date)}</td>
                  <td className="num">{n.number}</td>
                  <td className="max-w-[200px] truncate">{n.party_name}</td>
                  <td className="num text-muted-foreground">{n.original_number}</td>
                  <td className="text-muted-foreground">{n.reason || "—"}</td>
                  <td className="num text-right">{money(n.totals?.taxable)}</td>
                  <td className="num text-right">
                    {money((n.totals?.igst || 0) + (n.totals?.cgst || 0) + (n.totals?.sgst || 0))}
                  </td>
                  <td className="num text-right font-medium">{money(n.amount)}</td>
                  <td className="text-right">
                    <button className="text-[11px] text-primary underline" data-testid={`note-pdf-${n.id}`}
                      onClick={() => openInvoicePdf(n.id, n.number, "download").catch((e) => toast.error(e.message))}>
                      PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="note-editor-dialog">
          <DialogHeader><DialogTitle className="font-display">New {tab}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest">Original {isCredit ? "sales invoice" : "purchase bill"}</Label>
                <Select value={srcId} onValueChange={pickSource}>
                  <SelectTrigger data-testid="note-source-select"><SelectValue placeholder="Select document" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {sources.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.number} · {v.party_name} · {money(v.amount)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest">Note date</Label>
                <Input type="date" value={head.date} onChange={(e) => setHead({ ...head, date: e.target.value })}
                  data-testid="note-date-input" />
              </div>
            </div>

            {src ? (
              <>
                <table className="dense-table w-full" data-testid="note-lines-table">
                  <thead>
                    <tr><th>Item</th><th className="text-right">Invoiced</th><th className="text-right">Already returned</th>
                      <th className="text-right">Available</th><th className="text-right">Rate</th>
                      <th className="text-right">GST</th><th className="w-28 text-right">Return qty</th></tr>
                  </thead>
                  <tbody>
                    {src.lines.map((l) => (
                      <tr key={l.line_index}>
                        <td>{l.item_name}</td>
                        <td className="num text-right">{l.qty}</td>
                        <td className="num text-right text-muted-foreground">{l.returned}</td>
                        <td className="num text-right">{l.available}</td>
                        <td className="num text-right">{money(l.rate)}</td>
                        <td className="num text-right">{l.gst_rate}%</td>
                        <td>
                          <Input type="number" min="0" max={l.available} className="num h-8 border-0 text-right"
                            data-testid={`note-qty-input-${l.line_index}`} value={qty[l.line_index] ?? ""}
                            onChange={(e) => setQty({ ...qty, [l.line_index]: e.target.value })} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-widest">Reason</Label>
                    <Select value={head.reason} onValueChange={(v) => setHead({ ...head, reason: v })}>
                      <SelectTrigger data-testid="note-reason-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-widest">Narration</Label>
                    <Input value={head.narration} onChange={(e) => setHead({ ...head, narration: e.target.value })}
                      data-testid="note-narration-input" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground" data-testid="note-estimate">
                  Estimated note value <span className="num font-medium text-foreground">{money(est)}</span> ·
                  {isCredit ? " reduces receivable and output tax, stock comes back in" : " reduces payable and input tax, stock goes out"}
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Select a document to load its line items.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="note-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={!src || busy} data-testid="note-post-btn">
              {busy ? "Posting…" : `Post ${tab.toLowerCase()}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
